import { percentual, valorCurto, valorExato, variacao } from '../visual';
import { Alerta, Classe, Coluna, Historico, Periodo, Unidade } from './tipos';

// O jeito de escrever cada número e cada data do painel. Só formata: nenhum
// número é calculado aqui — eles vêm prontos do MCP (suplen_mcp/painel.py).

export const CLASSES: [Classe, string][] = [
  ['critico', 'Crítico'],
  ['atencao', 'Atenção'],
  ['oportunidade', 'Oportunidade'],
  ['informativo', 'Informativo'],
];
export const ROTULO_DA_CLASSE = Object.fromEntries(CLASSES) as Record<Classe, string>;

export const NATUREZA: Record<Alerta['natureza'], string> = {
  fato: 'Fato',
  tendencia: 'Tendência',
  hipotese: 'Hipótese',
  previsao: 'Previsão',
};

export const reais = (v: number) => valorExato('reais', v);
export const reaisCurtos = (v: number) => valorCurto('reais', v);
export const quantidade = (v: number) =>
  v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

export function plural(n: number, um: string, varios: string): string {
  return `${quantidade(n)} ${n === 1 ? um : varios}`;
}

// "17/10 a 23/10": o período sem o ano.
export const curto = (p: Periodo) => `${p.inicio.slice(0, 5)} a ${p.fim.slice(0, 5)}`;

// ─── Dias úteis ──────────────────────────────────────────────────────────────

const doisDigitos = (n: number) => String(n).padStart(2, '0');
const isoDoDia = (d: Date) => `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}`;

// Domingo de Páscoa (Meeus/Jones/Butcher), como o `_pascoa` do MCP.
function pascoa(ano: number): Date {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const m = (32 + 2 * e + 2 * i - h - k) % 7;
  const n = Math.floor((a + 11 * h + 22 * m) / 451);
  const t = h + m - 7 * n + 114;
  return new Date(ano, Math.floor(t / 31) - 1, (t % 31) + 1, 12);
}

// Os feriados nacionais do MCP (suplen_mcp/calculo.py, `feriados_nacionais`):
// os de data fixa e a Sexta-feira Santa. Carnaval e Corpus Christi são ponto
// facultativo; estadual e municipal ficam de fora.
const FERIADOS_FIXOS = ['01-01', '04-21', '05-01', '09-07', '10-12', '11-02', '11-15', '11-20', '12-25'];

export function diaUtil(d: Date): boolean {
  if (d.getDay() === 0 || d.getDay() === 6) return false;
  if (FERIADOS_FIXOS.includes(isoDoDia(d).slice(5))) return false;
  const sexta = pascoa(d.getFullYear());
  sexta.setDate(sexta.getDate() - 2);
  return isoDoDia(sexta) !== isoDoDia(d);
}

// "2026-11-04" → "2026-10-01": o 1º dia útil do mês anterior ao de hoje, onde
// o painel abre (o dono, 04/11/2026: assim já aparecem os pedidos sem nota).
export function primeiroDiaUtilDoMesAnterior(hoje: string): string {
  const [ano, mes] = hoje.split('-').map(Number);
  const dia = new Date(ano, mes - 2, 1, 12);
  while (!diaUtil(dia)) dia.setDate(dia.getDate() + 1);
  return isoDoDia(dia);
}

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto',
  'setembro', 'outubro', 'novembro', 'dezembro'];

// "outubro", pelo início do período (dd/mm/aaaa).
export const nomeDoMes = (p: Periodo) => MESES[Number(p.inicio.slice(3, 5)) - 1] || '';

// Como dizer o período anterior em cada análise: "em setembro" (o mês passado
// inteiro), "de 01/10 a 02/10" (os mesmos dias do mês passado) ou as datas.
export function oAnterior(analise: string, anterior: Periodo): string {
  if (analise === 'mes_passado') return `em ${nomeDoMes(anterior)}`;
  if (analise === 'mes_corrente') return `de ${curto(anterior)} de ${nomeDoMes(anterior)}`;
  return `de ${curto(anterior)}`;
}

// "03/11 às 14:32", no fuso de quem olha.
export function dataHora(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dia = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${dia} às ${hora}`;
}

export function dataCurta(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// A variação que veio do servidor; sem base (anterior zero), não é número.
export function textoDaVariacao(v: number | null | undefined, sufixo = '%'): string {
  if (v == null) return 'sem base de comparação';
  return variacao(v, sufixo);
}

// Uma célula de tabela, pela unidade que o servidor deu à coluna.
export function celula(unidade: Unidade, valor: unknown): string {
  if (valor == null || valor === '') return '—';
  if (typeof valor !== 'number') return String(valor);
  if (unidade === 'reais') return reais(valor);
  if (unidade === 'percentual') return percentual(valor);
  if (unidade === 'variacao') return variacao(valor);
  return quantidade(valor);
}

// Um campo da lista de um alerta, pelo nome: valor em reais, percentual ou pontos.
export function campo(chave: string, valor: unknown): string {
  if (Array.isArray(valor)) return valor.join(', ');
  if (typeof valor !== 'number') return String(valor ?? '');
  if (/percentual/.test(chave)) return percentual(valor);
  if (/pontos/.test(chave)) return `${quantidade(valor)} p.p.`;
  if (/venda|valor|ticket|preco|diferenca|meta|realizado|projecao|falta|necessario|lucro|efeito/.test(chave))
    return reais(valor);
  return quantidade(valor);
}

// Os campos vêm sem acento (são nomes de campo): a tela acentua as palavras.
const ACENTOS: Record<string, string> = {
  analise: 'análise',
  aprovacao: 'aprovação',
  codigo: 'código',
  condicao: 'condição',
  diferenca: 'diferença',
  emissao: 'emissão',
  integracao: 'integração',
  media: 'média',
  medio: 'médio',
  minima: 'mínima',
  minimo: 'mínimo',
  nao: 'não',
  necessario: 'necessário',
  numero: 'número',
  participacao: 'participação',
  periodo: 'período',
  preco: 'preço',
  previsao: 'previsão',
  projecao: 'projeção',
  reducoes: 'reduções',
  regiao: 'região',
  situacao: 'situação',
  ultima: 'última',
  ultimo: 'último',
  uteis: 'úteis',
  variacao: 'variação',
};

// O "percentual" do fim sai: o valor já vem com "%" ("Participação do maior: 58,3%").
export function cabecalho(chave: string): string {
  const palavras = chave.split('_').filter(Boolean);
  if (palavras.length > 1 && palavras[palavras.length - 1] === 'percentual') palavras.pop();
  const texto = palavras.map((p) => ACENTOS[p] || p).join(' ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

// Um número do alerta lido como período atual contra o anterior.
export interface NumeroComparado {
  base: string;
  chave: string; // a do atual ("venda_atual"): o porquê do número aponta para ela
  rotulo: string;
  atual: string;
  anterior: string;
  variacao?: number;
  sufixo?: string;
  diferenca?: number;
}

// Os números de um alerta: os pares atual × anterior (venda, pedidos,
// margem…), cada um com a variação que veio dele, e o resto, cru. A variação,
// os pontos e a diferença sem nome são do primeiro par, o indicador do alerta.
export function numerosDoAlerta(valores: Record<string, unknown>): {
  comparados: NumeroComparado[];
  outros: [string, unknown][];
} {
  const simples = Object.entries(valores || {}).filter(([, v]) => v != null && typeof v !== 'object');
  const mapa = Object.fromEntries(simples);
  const usados = new Set<string>();
  const comparados: NumeroComparado[] = [];
  for (const [k, v] of simples) {
    const m = /^(.+)_atual(_.+)?$/.exec(k);
    const chaveAnterior = m && `${m[1]}_anterior${m[2] || ''}`;
    if (!m || !chaveAnterior || !(chaveAnterior in mapa)) continue;
    usados.add(k);
    usados.add(chaveAnterior);
    comparados.push({
      base: m[1],
      chave: k,
      rotulo: cabecalho(m[1]),
      atual: campo(k, v),
      anterior: campo(chaveAnterior, mapa[chaveAnterior]),
    });
  }
  for (const [k, v] of simples) {
    if (typeof v !== 'number' || usados.has(k)) continue;
    const nomeada = /^variacao_(?:d[aoe]_)?(.+)_percentual$/.exec(k);
    const alvo = ['variacao_percentual', 'pontos', 'diferenca'].includes(k)
      ? comparados[0]
      : nomeada
        ? comparados.find((c) => c.base === nomeada[1])
        : undefined;
    if (!alvo) continue;
    if (k === 'diferenca') {
      if (alvo.diferenca !== undefined) continue;
      alvo.diferenca = v;
    } else {
      if (alvo.variacao !== undefined) continue;
      alvo.variacao = v;
      alvo.sufixo = k === 'pontos' ? ' p.p.' : '%';
    }
    usados.add(k);
  }
  return { comparados, outros: simples.filter(([k]) => !usados.has(k)) };
}

// ─── Acompanhamento ──────────────────────────────────────────────────────────

export type Tom = 'novo' | 'reaberto' | 'persistente';

// Novo, reaberto ou persistente, pelo histórico da análise programada.
export function estadoDoAlerta(h?: Historico): { tom: Tom; rotulo: string; detalhe: string } | null {
  if (!h) return null;
  if (h.vezes <= 1) return { tom: 'novo', rotulo: 'Novo', detalhe: 'Apareceu pela primeira vez nesta análise.' };
  const desde = `desde ${dataCurta(h.desde)}`;
  const vezes = `visto em ${plural(h.vezes, 'análise', 'análises')}`;
  if (h.reaberto_em)
    return {
      tom: 'reaberto',
      rotulo: 'Reaberto',
      detalhe: `Voltou em ${dataCurta(h.reaberto_em)} depois de resolvido (${plural(h.reaberturas || 1, 'vez', 'vezes')}); primeiro ${desde}, ${vezes}.`,
    };
  return { tom: 'persistente', rotulo: `Persiste ${desde}`, detalhe: `Aberto ${desde}, ${vezes}.` };
}

// ─── Alertas ─────────────────────────────────────────────────────────────────

export interface GrupoDeAlertas {
  chave: string;
  indicador: string;
  classe: Classe;
  alertas: Alerta[];
}

// Na mesma classe, alertas do mesmo indicador (dez vendedores em queda) viram um
// grupo que abre: a lista não vira um mural. Com menos de `minimo`, ficam soltos.
export function agrupar(alertas: Alerta[], minimo = 3): GrupoDeAlertas[] {
  const grupos: GrupoDeAlertas[] = [];
  const contagem = new Map<string, number>();
  alertas.forEach((a) => {
    const k = `${a.classe}|${a.indicador}`;
    contagem.set(k, (contagem.get(k) || 0) + 1);
  });
  alertas.forEach((a) => {
    const k = `${a.classe}|${a.indicador}`;
    if ((contagem.get(k) || 0) < minimo) {
      grupos.push({ chave: a.chave, indicador: a.indicador, classe: a.classe, alertas: [a] });
      return;
    }
    const grupo = grupos.find((g) => g.chave === k);
    if (grupo) grupo.alertas.push(a);
    else grupos.push({ chave: k, indicador: a.indicador, classe: a.classe, alertas: [a] });
  });
  return grupos;
}

// ─── Tabelas ─────────────────────────────────────────────────────────────────

type Linha = Record<string, string | number | null>;

const semAcento = (t: string) =>
  t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function filtrar(linhas: Linha[], termo: string, colunas: Coluna[]): Linha[] {
  const busca = semAcento(termo.trim());
  if (!busca) return linhas;
  const textos = colunas.filter((c) => c.unidade === 'texto').map((c) => c.campo);
  return linhas.filter((l) => textos.some((c) => semAcento(String(l[c] ?? '')).includes(busca)));
}

// Vazio (sem base) vai para o fim nos dois sentidos.
export function ordenar(linhas: Linha[], campo: string, crescente: boolean): Linha[] {
  return [...linhas].sort((a, b) => {
    const x = a[campo];
    const y = b[campo];
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    const ordem =
      typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x).localeCompare(String(y), 'pt-BR');
    return crescente ? ordem : -ordem;
  });
}

// CSV para o Excel em português: ponto e vírgula e vírgula decimal, com BOM.
export function paraCsv(colunas: Coluna[], linhas: Linha[]): string {
  const escapar = (t: string) => (/[;"\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t);
  const valor = (v: unknown) =>
    v == null ? '' : typeof v === 'number' ? String(v).replace('.', ',') : escapar(String(v));
  const cabecalhos = colunas.map((c) => escapar(c.rotulo)).join(';');
  const corpo = linhas.map((l) => colunas.map((c) => valor(l[c.campo])).join(';'));
  return `﻿${[cabecalhos, ...corpo].join('\r\n')}`;
}
